import { type ChangeEvent, useRef } from 'react';

import { useIsomorphicLayoutEffect } from 'ahooks';

import { AdornedInput, Input } from '@zillow/constellation-10';

import { formatWholeNumber } from './grm';

type WholeNumberInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isCurrency: boolean;
};

const isSignificant = (character: string) => /[\d.]/.test(character);

const countSignificant = (value: string) => [...value].filter(isSignificant).length;

const caretAfterSignificant = (value: string, count: number) => {
  let seen = 0;
  for (let index = 0; index < value.length; index++) {
    if (seen === count) {
      return index;
    }
    if (isSignificant(value[index])) {
      seen++;
    }
  }
  return value.length;
};

const formatWhileTyping = (raw: string) => {
  const dotIndex = raw.indexOf('.');
  if (dotIndex === -1) {
    return formatWholeNumber(raw);
  }
  const fraction = raw.slice(dotIndex + 1).replace(/\D/g, '');
  return `${formatWholeNumber(raw.slice(0, dotIndex))}.${fraction}`;
};

const WholeNumberInput = ({ value, onChange, onBlur, isCurrency }: WholeNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (pendingCaret.current !== null && inputRef.current === document.activeElement) {
      const caret = caretAfterSignificant(value, pendingCaret.current);
      inputRef.current?.setSelectionRange(caret, caret);
    }
    pendingCaret.current = null;
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let raw = event.target.value;
    let caret = event.target.selectionStart ?? raw.length;
    const { inputType } = event.nativeEvent as InputEvent;

    // Deleting only a comma leaves the digits unchanged, so delete the neighboring digit instead.
    if (raw.length < value.length && raw.replace(/,/g, '') === value.replace(/,/g, '')) {
      if (inputType === 'deleteContentBackward' && caret > 0) {
        raw = raw.slice(0, caret - 1) + raw.slice(caret);
        caret--;
      } else if (inputType === 'deleteContentForward') {
        raw = raw.slice(0, caret) + raw.slice(caret + 1);
      }
    }

    pendingCaret.current = countSignificant(raw.slice(0, caret));
    onChange(formatWhileTyping(raw));
  };

  const handleBlur = () => {
    onChange(formatWholeNumber(value));
    onBlur();
  };

  const inputProps = {
    ref: inputRef,
    inputMode: 'numeric' as const,
    maxLength: 15,
    value,
    onChange: handleChange,
    onBlur: handleBlur,
  };

  return isCurrency ? (
    <AdornedInput
      input={<AdornedInput.Input {...inputProps} />}
      startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
      fluid
    />
  ) : (
    <Input {...inputProps} />
  );
};

export default WholeNumberInput;
